import { useMemo, forwardRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';
import {
  getChartColors,
  chartDefaults,
  formatCurrency,
  chartContainerStyle,
} from '../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

/**
 * BeforeAfterChart - Before/After Treatment Visualization
 *
 * Shows wasted energy before treatment (red bars) and
 * projected savings after treatment (green bars).
 * Includes vertical line at treatment year.
 *
 * Based on 2017 IPP Calculator visualization style.
 *
 * @param {Object} beforeAfterData - Data from generateBeforeAfterData()
 */
const BeforeAfterChart = forwardRef(function BeforeAfterChart({ beforeAfterData }, ref) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getChartColors(theme.palette.mode);

  const chartData = useMemo(() => {
    if (!beforeAfterData) return null;

    const { labels, datasets, treatmentYearIndex } = beforeAfterData;

    return {
      labels,
      datasets: [
        {
          label: 'Wasted kWh (Before Treatment)',
          data: datasets.beforeTreatment,
          backgroundColor: 'rgba(244, 67, 54, 0.7)',
          borderColor: 'rgba(244, 67, 54, 1)',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.85,
        },
        {
          label: 'Saved kWh (After Treatment)',
          data: datasets.afterTreatment,
          backgroundColor: 'rgba(76, 175, 80, 0.7)',
          borderColor: 'rgba(76, 175, 80, 1)',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.85,
        },
      ],
    };
  }, [beforeAfterData]);

  const options = useMemo(() => {
    if (!beforeAfterData) return {};

    const { treatmentYearIndex, treatmentYear } = beforeAfterData;

    return {
      ...chartDefaults,
      plugins: {
        ...chartDefaults.plugins,
        legend: {
          ...chartDefaults.plugins.legend,
          display: true,
        },
        tooltip: {
          ...chartDefaults.plugins.tooltip,
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y.toLocaleString();
              return `${label}: ${value} kWh`;
            },
          },
        },
        annotation: {
          annotations: {
            treatmentLine: {
              type: 'line',
              xMin: treatmentYearIndex + 0.5,
              xMax: treatmentYearIndex + 0.5,
              borderColor: colors.primary,
              borderWidth: 3,
              borderDash: [6, 3],
              label: {
                display: true,
                content: `Treatment (Year ${treatmentYear})`,
                position: 'start',
                backgroundColor: 'rgba(160, 206, 78, 0.95)',
                color: '#0A0A0A',
                font: {
                  size: 12,
                  weight: 700,
                },
                padding: { x: 10, y: 6 },
                borderRadius: 4,
                yAdjust: -10,
              },
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        ...chartDefaults.scales,
        x: {
          ...chartDefaults.scales.x,
          stacked: false,
          grid: {
            display: false,
          },
          ticks: {
            ...chartDefaults.scales.x?.ticks,
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 15,
          },
        },
        y: {
          ...chartDefaults.scales.y,
          beginAtZero: true,
          title: {
            display: true,
            text: 'kWh',
            color: colors.grey,
            font: {
              size: 11,
              weight: 600,
            },
          },
          ticks: {
            ...chartDefaults.scales.y.ticks,
            callback: (value) => value.toLocaleString(),
          },
        },
      },
    };
  }, [beforeAfterData]);

  if (!chartData || !beforeAfterData) {
    return null;
  }

  const { totals, metadata } = beforeAfterData;

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          Energy Impact: Before vs After Treatment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {metadata.equipmentCount > 1
            ? `Portfolio total across ${metadata.equipmentCount} equipment items (${metadata.totalTonnage?.toLocaleString()} tons)`
            : 'Shows wasted energy from degradation before treatment (red) and projected savings after treatment (green)'}
        </Typography>
      </Box>

      {/* Chart Container */}
      <Box sx={{ ...chartContainerStyle, height: 350 }}>
        <Bar ref={ref} data={chartData} options={options} />
      </Box>

      {/* Summary Stats */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          flexWrap: 'wrap',
          gap: 2,
          mt: 3,
          p: 2,
          borderRadius: 2,
          background: 'rgba(78, 205, 196, 0.08)',
          border: '1px solid rgba(78, 205, 196, 0.2)',
        }}
      >
        <Box sx={{ textAlign: 'center', minWidth: 140 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Energy Wasted (Past {metadata.currentAge} Years)
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: theme.palette.error.main,
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {totals.totalWastedKwh.toLocaleString()} kWh
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatCurrency(totals.totalWastedDollars)}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', minWidth: 140 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Projected Savings (Next {metadata.projectionYears} Years)
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: isDark ? theme.palette.success.main : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {totals.totalSavedKwh.toLocaleString()} kWh
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatCurrency(totals.totalSavedDollars)}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', minWidth: 140 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Net Lifetime Benefit
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: isDark ? colors.primary : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(totals.netLifetimeBenefit)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Treatment at year {metadata.currentAge}
          </Typography>
        </Box>
      </Box>

      {/* Insight Box */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)',
          border: '1px solid rgba(160, 206, 78, 0.3)',
        }}
      >
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          <strong>Insight:</strong> Over the past {metadata.currentAge} years,{' '}
          {metadata.equipmentCount > 1
            ? `this portfolio of ${metadata.equipmentCount} units`
            : 'this equipment'}{' '}
          has wasted{' '}
          <span style={{ color: isDark ? theme.palette.error.main : theme.palette.text.primary, fontWeight: 600 }}>
            {formatCurrency(totals.totalWastedDollars)}
          </span>{' '}
          due to thermal degradation. After treatment, projected savings over the next{' '}
          {metadata.projectionYears} years are{' '}
          <span style={{ color: isDark ? theme.palette.success.main : theme.palette.text.primary, fontWeight: 600 }}>
            {formatCurrency(totals.totalSavedDollars)}
          </span>.
        </Typography>
      </Box>
    </Box>
  );
});

export default BeforeAfterChart;
